// AI Help Chatbot — Context-Aware, Agentic, Multi-Modal
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { MessageCircle, X, Send, Loader2, Paperclip, AlertTriangle, Sparkles, Database, MoreHorizontal, Search } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = window.location.origin;

// Pages that trigger proactive nudges after 2 min inactivity
const NUDGE_PAGES = {
  '/admin': "It looks like you're in the Admin Portal. Need help with user management, permissions, or settings? I'm here!",
  '/admin/config': "Configuring your system? I can walk you through branding, email templates, or maintenance scripts.",
  '/reports/custom': "Building a custom report? I can help you choose the right data source and set up filters.",
  '/invoices': "Working on invoices? I can help you create, approve, and send invoices to your owners.",
  '/sales/opportunities': "Managing sale entries? I can help with deliverables, vetting procedures, or hip numbers.",
  '/crm': "In your CRM? I can help you add accounts, set up associations, or manage contact preferences.",
};

const NUDGE_DELAY_MS = 120000; // 2 minutes

const QUICK_ACTIONS = [
  { label: 'How do I add a horse?',    msg: 'How do I add a horse?' },
  { label: 'Creating a sale entry',     msg: 'How do I create a sale entry?' },
  { label: 'Invoice creation',          msg: 'How do I create an invoice?' },
  { label: 'Owner portal access',       msg: 'How does the owner portal work?' },
];

const DATA_QUICK_EXAMPLES = [
  { label: "Horse's covering sire",     msg: "What is [horse name]'s covering sire?" },
  { label: 'Show unpaid invoices',      msg: 'Show me all unpaid invoices' },
  { label: 'Horses in a sale',          msg: 'Which horses are in the [sale name] sale?' },
  { label: 'Recent activities',         msg: 'Show recent activities for [account name]' },
];

const FUNCTION_LABELS = {
  search_horses: 'Horse Search',
  get_horse_detail: 'Horse Detail',
  get_horses_in_sale: 'Sale Entries',
  get_sale_opportunities: 'Sale Opportunities',
  get_account_summary: 'Account Summary',
  get_pending_deliverables: 'Pending Deliverables',
  get_vetting_for_horse: 'Vetting Records',
  get_invoices: 'Invoices',
  get_recent_activities: 'Recent Activities',
  get_sale_overview: 'Sale Overview',
};

const PAGE_LABELS = {
  '/horses': 'Horses (Virtual Stable)',
  '/sales': 'Active Sales',
  '/sales/opportunities': 'Sale Opportunities',
  '/crm': 'CRM — Accounts & Contacts',
  '/invoices': 'Invoices',
  '/reports': 'Reports',
  '/activities': 'Activities',
  '/admin': 'Tenant Admin Portal',
  '/owner-portal': 'Owner Portal',
};

const getPageLabel = (pathname) => {
  for (const [path, label] of Object.entries(PAGE_LABELS)) {
    if (pathname.startsWith(path)) return label;
  }
  return pathname;
};

const GREETINGS = {
  support: "Hi! I'm your Green Door assistant. I know this platform inside out — ask me anything.",
  data_query: "Hi! I'm your Green Door assistant. Ask me anything about your Accounts, Contacts, or Horses.",
};

const AIChatbot = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('support'); // 'support' | 'data_query'
  // Separate message histories per mode
  const [messagesByMode, setMessagesByMode] = useState({
    support:     [{ role: 'assistant', content: GREETINGS.support }],
    data_query:  [{ role: 'assistant', content: GREETINGS.data_query }],
  });
  const messages = messagesByMode[mode];
  const setMessages = (updater) => setMessagesByMode(prev => ({
    ...prev,
    [mode]: typeof updater === 'function' ? updater(prev[mode]) : updater,
  }));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentiment, setSentiment] = useState('neutral');
  const [pendingImage, setPendingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [showEscalatePrompt, setShowEscalatePrompt] = useState(false);
  // Recent data queries — persisted in localStorage
  const [recentQueries, setRecentQueries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gd_recent_data_queries') || '[]'); }
    catch { return []; }
  });
  const [showRecent, setShowRecent] = useState(false);
  // Stable session IDs per mode — keeps support and Ask Data histories isolated server-side
  const sessionIds = useRef({
    support:    `support_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    data_query: `data_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  });
  const nudgeTimerRef = useRef(null);
  const nudgedRef = useRef(false); // only nudge once per page visit

  // Proactive nudge: open chatbot with a tip after 2 min inactivity on complex pages
  useEffect(() => {
    const nudgeMsg = Object.entries(NUDGE_PAGES).find(([path]) =>
      location.pathname.startsWith(path)
    )?.[1];

    if (!nudgeMsg) return; // not a nudge page

    const resetTimer = () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      if (nudgedRef.current || isOpen) return; // already nudged or already open
      nudgeTimerRef.current = setTimeout(() => {
        if (!nudgedRef.current && !isOpen) {
          nudgedRef.current = true;
          setMode('support');  // always nudge on the Support tab
          setIsOpen(true);
          setMessagesByMode(prev => ({
            ...prev,
            support: [...(prev.support || []), { role: 'assistant', content: nudgeMsg }],
          }));
        }
      }, NUDGE_DELAY_MS);
    };

    resetTimer();
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);

    return () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, [location.pathname, isOpen]);

  // Reset nudge state when navigating to a new page
  useEffect(() => {
    nudgedRef.current = false;
  }, [location.pathname]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pageContext = getPageLabel(location.pathname);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getUser = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  };

  const getToken = () => localStorage.getItem('token');

  const saveRecentQuery = (text) => {
    setRecentQueries(prev => {
      const deduped = prev.filter(q => q !== text);
      const updated = [text, ...deduped].slice(0, 5);
      localStorage.setItem('gd_recent_data_queries', JSON.stringify(updated));
      return updated;
    });
  };

  const removeRecentQuery = (index) => {
    setRecentQueries(prev => {
      const updated = prev.filter((_, i) => i !== index);
      localStorage.setItem('gd_recent_data_queries', JSON.stringify(updated));
      return updated;
    });
  };

  const clearAllRecentQueries = () => {
    localStorage.removeItem('gd_recent_data_queries');
    setRecentQueries([]);
    setShowRecent(false);
  };

  const sendMessage = async (text = input, imgBase64 = pendingImage) => {
    const msgText = text.trim();
    if (!msgText && !imgBase64) return;
    if (loading) return;

    const userMsg = { role: 'user', content: msgText, hasImage: !!imgBase64 };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingImage(null);
    setImagePreview(null);
    setLoading(true);
    setShowEscalatePrompt(false);
    setShowRecent(false);

    try {
      if (mode === 'data_query') {
        // Ask Data mode — streaming SSE with client-side typing animation
        saveRecentQuery(msgText);

        // Show "Looking up data..." spinner IMMEDIATELY — visible before fetch even starts
        setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true, thinking: true, dataFunction: '', dataIntent: '' }]);
        setLoading(false);

        const res = await fetch(`${BACKEND_URL}/api/chatbot/data/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({
            question: msgText,
            session_id: sessionIds.current[mode],
            history: messages.slice(-8).map(m => ({ role: m.role, content: m.content })),
          }),
        });

        if (!res.ok) throw new Error(`Chat service error: ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let tokenAccumulated = '';
        let doneEvt = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const evt = JSON.parse(line.slice(6));
              if (evt.type === 'thinking') {
                setMessages(prev => {
                  const msgs = [...prev];
                  msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: '', thinking: true, dataFunction: evt.function };
                  return msgs;
                });
              } else if (evt.type === 'token') {
                tokenAccumulated += evt.content;
              } else if (evt.type === 'done') {
                doneEvt = evt;
              }
            } catch { /* malformed SSE line */ }
          }
        }

        // Stream closed — ALWAYS type out the response character by character
        if (doneEvt) {
          const finalText = doneEvt.response || tokenAccumulated;
          const CHARS_PER_TICK = 4;
          const TICK_MS = 18;
          let pos = 0;
          await new Promise(resolve => {
            const interval = setInterval(() => {
              pos = Math.min(pos + CHARS_PER_TICK, finalText.length);
              setMessages(prev => {
                const msgs = [...prev];
                msgs[msgs.length - 1] = {
                  ...msgs[msgs.length - 1],
                  content: finalText.slice(0, pos),
                  thinking: false,
                  streaming: pos < finalText.length,
                };
                return msgs;
              });
              if (pos >= finalText.length) {
                clearInterval(interval);
                setMessages(prev => {
                  const msgs = [...prev];
                  msgs[msgs.length - 1] = {
                    role: 'assistant', content: finalText, streaming: false,
                    dataFunction: doneEvt.function, dataIntent: doneEvt.intent,
                  };
                  return msgs;
                });
                resolve();
              }
            }, TICK_MS);
          });
        }
      } else {
        // Support mode — existing chatbot
        const res = await fetch(`${BACKEND_URL}/api/chatbot/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({
            message: msgText || (imgBase64 ? 'Please analyze this screenshot and help me.' : ''),
            page_context: pageContext,
            session_id: sessionIds.current[mode],
            history: messages.slice(-12).map(m => ({ role: m.role, content: m.content })),
            image_base64: imgBase64 || null,
          }),
        });
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        if (data.sentiment) setSentiment(data.sentiment);
        if (data.sentiment === 'negative') setShowEscalatePrompt(true);
        if (data.suggested_action === 'upload_screenshot') {
          setTimeout(() => fileInputRef.current?.click(), 500);
        }
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble right now. Please try again." }]);
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1]; // strip data: prefix
      setPendingImage(base64);
      setImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleEscalate = async () => {
    setEscalating(true);
    try {
      const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
      const res = await fetch(`${BACKEND_URL}/api/chatbot/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          summary: lastUserMsg?.content || 'User requested escalation',
          conversation: messages.slice(-20).map(m => ({ role: m.role, content: m.content })),
          page_context: pageContext,
          sentiment,
          category: 'general',
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Your support ticket has been created (ID: ${data.ticket_id?.slice(0, 8)}). A specialist will follow up with you shortly. Is there anything else I can help with in the meantime?`,
      }]);
      setShowEscalatePrompt(false);
    } catch {
      toast.error('Failed to create ticket');
    }
    setEscalating(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (!isOpen) {
    return (
      <div className="mobile-chatbot fixed bottom-6 right-6 z-[9999]">
        <Button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg text-white"
          data-testid="chatbot-open-button"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mobile-chatbot fixed bottom-6 right-6 z-[9999] w-96 flex flex-col rounded-xl shadow-2xl bg-white border overflow-hidden"
         style={{ height: '560px' }}>
      {/* Header */}
      <div className="bg-primary text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <p className="text-sm font-semibold">Green Door Assistant</p>
        </div>
        <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-8 w-8 p-0" onClick={() => setIsOpen(false)}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Mode Toggle */}
      <div className="px-3 py-2 border-b bg-gray-50 flex gap-1 flex-shrink-0">
        <button
          data-testid="chatbot-mode-support"
          onClick={() => setMode('support')}
          className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-colors flex items-center justify-center gap-1 ${
            mode === 'support' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <MessageCircle className="w-3 h-3" />
          Support
        </button>
        <button
          data-testid="chatbot-mode-data"
          onClick={() => setMode('data_query')}
          className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-colors flex items-center justify-center gap-1 ${
            mode === 'data_query' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Search className="w-3 h-3" />
          Ask Your Data
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>
              {msg.hasImage && <div className="text-xs opacity-70 mb-1">📎 Screenshot attached</div>}
              {msg.dataFunction && msg.dataFunction !== 'unsupported' && (
                <div className="text-xs text-primary font-medium mb-1 flex items-center gap-1">
                  <Search className="w-3 h-3" />
                  {FUNCTION_LABELS[msg.dataFunction] || msg.dataFunction}
                </div>
              )}
              {msg.thinking && !msg.content ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="text-xs">Looking up data...</span>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                  {msg.streaming && msg.content && (
                    <span className="inline-block w-1.5 h-3.5 bg-foreground/70 ml-0.5 animate-pulse align-middle" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-muted rounded-xl px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              {mode === 'data_query' ? 'Querying your data...' : 'Thinking...'}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Escalation prompt (support mode only) */}
      {mode === 'support' && showEscalatePrompt && (
        <div className="px-3 py-2 bg-orange-50 border-t border-orange-200 flex items-center justify-between gap-2 flex-shrink-0">
          <p className="text-xs text-orange-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Need more help?
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-700"
            onClick={handleEscalate} disabled={escalating}>
            {escalating ? 'Creating...' : 'Create Support Ticket'}
          </Button>
        </div>
      )}

      {/* Image preview (support mode only) */}
      {mode === 'support' && imagePreview && (
        <div className="px-3 py-2 border-t bg-muted/30 flex items-center gap-2 flex-shrink-0">
          <img src={imagePreview} alt="screenshot" className="w-10 h-10 object-cover rounded" />
          <p className="text-xs text-muted-foreground flex-1">Screenshot ready to send</p>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setPendingImage(null); setImagePreview(null); }}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Quick actions / Data examples (only on first message) */}
      {messages.length === 1 && (
        <div className="px-3 pb-2 flex-shrink-0 space-y-2">
          {/* Recent queries — data mode only */}
          {mode === 'data_query' && recentQueries.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1.5 flex items-center gap-1">
                <span className="w-3 h-3 text-primary">↩</span> Recent
                <button onClick={clearAllRecentQueries} className="ml-auto text-[10px] text-muted-foreground hover:text-red-500 transition-colors">Clear all</button>
              </p>
              <div className="flex flex-col gap-1">
                {recentQueries.slice(0, 4).map((q, i) => (
                  <div key={i} className="flex items-center gap-1 group">
                    <button onClick={() => sendMessage(q)}
                      data-testid={`recent-query-${i}`}
                      className="flex-1 text-left text-xs px-2.5 py-1.5 rounded-md bg-muted hover:bg-primary hover:text-white text-muted-foreground border truncate transition-colors">
                      {q}
                    </button>
                    <button onClick={() => removeRecentQuery(i)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 text-sm leading-none px-1 transition-opacity"
                      title="Remove">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Quick action chips */}
          <div className="flex gap-1 flex-wrap">
            {(mode === 'data_query' ? DATA_QUICK_EXAMPLES : QUICK_ACTIONS).map((qa, i) => (
              <button key={i} onClick={() => sendMessage(qa.msg)}
                className="text-xs px-2 py-1 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground border">
                {qa.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent queries panel — shown when "..." is clicked in data mode */}
      {mode === 'data_query' && showRecent && recentQueries.length > 0 && (
        <div className="px-3 pb-2 border-t bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between mt-2 mb-1.5">
            <p className="text-xs text-muted-foreground font-medium">Recent queries</p>
            <button onClick={clearAllRecentQueries} className="text-[10px] text-muted-foreground hover:text-red-500 transition-colors">Clear all</button>
          </div>
          <div className="flex flex-col gap-1">
            {recentQueries.map((q, i) => (
              <div key={i} className="flex items-center gap-1 group">
                <button onClick={() => { sendMessage(q); setShowRecent(false); }}
                  data-testid={`recent-query-panel-${i}`}
                  className="flex-1 text-left text-xs px-2.5 py-1.5 rounded-md bg-white hover:bg-primary hover:text-white text-muted-foreground border truncate transition-colors shadow-sm">
                  {q}
                </button>
                <button onClick={() => removeRecentQuery(i)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 text-sm leading-none px-1 transition-opacity"
                  title="Remove">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t flex items-center gap-2 flex-shrink-0 bg-white">
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        {mode === 'support' && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground" title="Attach screenshot"
            onClick={() => fileInputRef.current?.click()}>
            <Paperclip className="w-4 h-4" />
          </Button>
        )}
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={mode === 'data_query' ? "Ask about horses, sales, invoices..." : "Ask me anything..."}
          className="flex-1 h-8 text-sm"
          disabled={loading}
          data-testid="chatbot-input"
        />
        {mode === 'data_query' && recentQueries.length > 0 && (
          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${showRecent ? 'text-primary bg-green-50' : 'text-muted-foreground'}`}
            title="Recent queries"
            data-testid="chatbot-recent-toggle"
            onClick={() => setShowRecent(r => !r)}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        )}
        <Button size="sm" className="h-8 w-8 p-0 bg-primary hover:bg-primary/90 text-white"
          onClick={() => sendMessage()} disabled={loading || (!input.trim() && !pendingImage)}
          data-testid="chatbot-send">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default AIChatbot;
